import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select } from 'antd';
import Pagination from '../../components/Pagination';
import {
  PlusIcon,
  SearchIcon,
  CaretIcon,
  RefreshIcon,
  EditLineIcon,
  MoreVerticalIcon,
} from '../../assets/icons';

/* ------------------------------------------------------------------ *
 * Government Departments — listing (Build Hierarchy → Dept. Configuration).
 * Figma 1676-2781. Search / filter / sort a registered department table.
 * Demo data (no API yet), mirroring the other Build Hierarchy screens.
 * ------------------------------------------------------------------ */

interface Department {
  id: string;
  initials: string;
  /** Avatar palette (bg + text) for the initials badge. */
  avatar: string;
  name: string;
  summary: string;
  hierarchy: string;
  levels: number;
  /** Range label shown under the level count, e.g. "Apex to Tahsil". */
  span: string;
  active: boolean;
  updated: string;
}

const AVATARS = {
  blue: 'bg-[#e0edff] text-[#2563eb]',
  rose: 'bg-[#ffe4e6] text-[#e11d48]',
  green: 'bg-[#dcfce7] text-[#15803d]',
  teal: 'bg-[#ccfbf1] text-[#0f766e]',
  amber: 'bg-[#fef3c7] text-[#b45309]',
} as const;

const DEPARTMENTS: Department[] = [
  {
    id: 'rd',
    initials: 'RD',
    avatar: AVATARS.blue,
    name: 'Revenue & Land Reforms Department',
    summary: 'Nodal authority for land disputes, survey...',
    hierarchy: 'State Revenue (General Administration)',
    levels: 4,
    span: 'Apex to Tahsil',
    active: true,
    updated: '12 Oct 2023',
  },
  {
    id: 'hp',
    initials: 'HP',
    avatar: AVATARS.rose,
    name: 'Home & State Police Administration',
    summary: 'Law enforcement coordination, state...',
    hierarchy: 'Law Enforcement & Security',
    levels: 6,
    span: 'Apex to Station',
    active: true,
    updated: '28 Sep 2023',
  },
  {
    id: 'pr',
    initials: 'PR',
    avatar: AVATARS.green,
    name: 'Panchayati Raj & Rural Development',
    summary: 'Local block governance, Gram Panchayat...',
    hierarchy: 'Rural Local Governance',
    levels: 5,
    span: 'Apex to Village',
    active: true,
    updated: '15 Aug 2023',
  },
  {
    id: 'hw',
    initials: 'HW',
    avatar: AVATARS.teal,
    name: 'Health & Family Welfare Department',
    summary: 'Public health infrastructure, primary...',
    hierarchy: 'Public Health Services',
    levels: 5,
    span: 'Apex to Sub-center',
    active: true,
    updated: '04 Jul 2023',
  },
  {
    id: 'pw',
    initials: 'PW',
    avatar: AVATARS.amber,
    name: 'Public Works Department (Roads & Bridges)',
    summary: 'State highway engineering, public...',
    hierarchy: 'Infrastructure & Engineering',
    levels: 4,
    span: 'Apex to Sub-division',
    active: false,
    updated: '22 May 2023',
  },
];

/** Shared grid template so header + body rows stay aligned. */
const GRID =
  'grid grid-cols-[minmax(240px,1.7fr)_minmax(190px,1.3fr)_150px_150px_130px_100px] items-center';

/** Month index for parsing the "DD Mon YYYY" demo dates (for the sort options). */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const toTime = (d: string) => {
  const [day, mon, year] = d.split(' ');
  return new Date(Number(year), MONTHS.indexOf(mon), Number(day)).getTime();
};

const TERRITORY_OPTS = ['All Hierarchies', ...DEPARTMENTS.map((d) => d.hierarchy)];
const STATUS_OPTS = ['All Status', 'Active', 'Inactive'];
const SORT_OPTS = ['Recently Updated', 'Name (A–Z)', 'Oldest First'];

/** antd Select styled to match the toolbar pills (grey border, compact). */
const SELECT_CLS = 'dept-filter';

function StatusPill({ active }: { active: boolean }) {
  return active ? (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-[#a7f3d0] bg-[#ecfdf5] px-[11px] py-[5px] text-xs font-medium text-[#059669]">
      <span className="size-1.5 rounded-full bg-[#10b981]" />
      Active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-line-soft bg-[#f3f4f6] px-[11px] py-[5px] text-xs font-medium text-[#4b5563]">
      <span className="size-1.5 rounded-full bg-[#9ca3af]" />
      Inactive
    </span>
  );
}

function DepartmentRow({ dept, onEdit }: { dept: Department; onEdit: () => void }) {
  return (
    <div className={[GRID, 'border-t border-[#f3f4f6]'].join(' ')}>
      {/* Department name + avatar + summary */}
      <div className="flex items-center gap-3.5 px-6 py-4">
        <span
          className={[
            'flex size-10 shrink-0 items-center justify-center rounded-lg text-xs font-semibold',
            dept.avatar,
          ].join(' ')}
        >
          {dept.initials}
        </span>
        <div className="flex min-w-0 flex-col gap-0.5">
          <p className="text-[13px] font-semibold leading-[16.25px] text-[#111827]">{dept.name}</p>
          <p className="line-clamp-2 text-xs leading-4 text-[#9ca3af]">{dept.summary}</p>
        </div>
      </div>

      {/* Territory hierarchy */}
      <div className="px-6 py-4">
        <span className="inline-flex rounded-md bg-brand-shade px-2.5 py-1.5 text-xs leading-4 text-ink">
          {dept.hierarchy}
        </span>
      </div>

      {/* Ranks / designations */}
      <div className="px-6 py-4 text-xs leading-4">
        <span className="font-bold text-[#111827]">{dept.levels} Levels </span>
        <span className="text-[#9ca3af]">(Apex to {dept.span.replace('Apex to ', '')})</span>
      </div>

      {/* Status */}
      <div className="px-6 py-4">
        <StatusPill active={dept.active} />
      </div>

      {/* Last updated */}
      <div className="px-6 py-4 text-xs text-[#6b7280]">{dept.updated}</div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 px-6 py-4">
        <button
          type="button"
          onClick={onEdit}
          aria-label={`Edit ${dept.name}`}
          className="rounded p-1 text-[#6b7280] transition-colors hover:bg-brand-shade hover:text-brand"
        >
          <EditLineIcon className="size-4" />
        </button>
        <button
          type="button"
          aria-label={`More options for ${dept.name}`}
          className="flex size-6 items-center justify-center rounded text-[#9ca3af] transition-colors hover:bg-brand-shade hover:text-brand"
        >
          <MoreVerticalIcon className="h-3.5 w-auto" />
        </button>
      </div>
    </div>
  );
}

export default function DepartmentListingPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [territory, setTerritory] = useState(TERRITORY_OPTS[0]);
  const [status, setStatus] = useState(STATUS_OPTS[0]);
  const [sort, setSort] = useState(SORT_OPTS[0]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = DEPARTMENTS.filter((d) => {
      const matchesSearch =
        !q || d.name.toLowerCase().includes(q) || d.hierarchy.toLowerCase().includes(q);
      const matchesTerritory = territory === TERRITORY_OPTS[0] || d.hierarchy === territory;
      const matchesStatus =
        status === 'All Status' || (status === 'Active' ? d.active : !d.active);
      return matchesSearch && matchesTerritory && matchesStatus;
    });
    if (sort === 'Name (A–Z)') list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    else if (sort === 'Oldest First') list = [...list].sort((a, b) => toTime(a.updated) - toTime(b.updated));
    else list = [...list].sort((a, b) => toTime(b.updated) - toTime(a.updated));
    return list;
  }, [search, territory, status, sort]);

  // Reset to the first page whenever the filtered result set changes.
  useEffect(() => setPage(1), [search, territory, status, sort]);

  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageRows = rows.slice((page - 1) * pageSize, page * pageSize);

  const resetFilters = () => {
    setSearch('');
    setTerritory(TERRITORY_OPTS[0]);
    setStatus(STATUS_OPTS[0]);
    setSort(SORT_OPTS[0]);
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Header + CTA */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold tracking-[-0.3px] text-[#2563eb]">
            Government Departments
          </span>
          <h1 className="text-2xl font-bold leading-8 text-[#111827]">Government Departments</h1>
          <p className="text-xs leading-4 text-[#6b7280]">
            Manage registered government departments, jurisdictional hierarchies, designations, and
            administrative officers.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/admin/dept-configuration/new')}
          className="flex shrink-0 items-center gap-1.5 rounded-lg bg-brand px-4 py-2.5 text-sm font-semibold text-white shadow-card transition-colors hover:bg-brand-dark"
        >
          <PlusIcon className="size-4" />
          Create Department
        </button>
      </div>

      {/* Filter & search toolbar */}
      <div className="rounded-xl border border-[#e5e7eb] bg-white p-[17px] shadow-card">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[280px] flex-1">
            <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-[#9ca3af]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search department by name or hierarchy..."
              className="w-full rounded-lg border border-[#e5e7eb] bg-white py-2.5 pl-10 pr-4 text-xs text-ink placeholder:text-[#9ca3af] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
            />
          </div>
          <Select
            className={SELECT_CLS}
            value={territory}
            onChange={setTerritory}
            options={TERRITORY_OPTS.map((o) => ({ value: o, label: o }))}
            labelRender={(p) => `Territory: ${p.label}`}
            suffixIcon={<CaretIcon className="size-3.5 text-[#6b7280]" />}
            popupMatchSelectWidth={false}
          />
          <Select
            className={SELECT_CLS}
            value={status}
            onChange={setStatus}
            options={STATUS_OPTS.map((o) => ({ value: o, label: o }))}
            labelRender={(p) => `Status: ${p.label}`}
            suffixIcon={<CaretIcon className="size-3.5 text-[#6b7280]" />}
            popupMatchSelectWidth={false}
          />
          <Select
            className={SELECT_CLS}
            value={sort}
            onChange={setSort}
            options={SORT_OPTS.map((o) => ({ value: o, label: o }))}
            labelRender={(p) => `Sort: ${p.label}`}
            suffixIcon={<CaretIcon className="size-3.5 text-[#6b7280]" />}
            popupMatchSelectWidth={false}
          />
          <button
            type="button"
            onClick={resetFilters}
            aria-label="Refresh list"
            className="rounded-lg border border-[#e5e7eb] bg-white p-2.5 text-[#6b7280] transition-colors hover:bg-brand-shade hover:text-brand"
          >
            <RefreshIcon className="size-4" />
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-[#e5e7eb] bg-white shadow-card">
        <div className="overflow-x-auto">
          <div className="min-w-[900px]">
            {/* Header */}
            <div className={[GRID, 'bg-[#f9fafb]/50 border-b border-[#f3f4f6]'].join(' ')}>
              {(
                [
                  ['DEPARTMENT NAME', 'justify-start'],
                  ['TERRITORY HIERARCHY', 'justify-start'],
                  ['RANKS / DESIGNATIONS', 'justify-start'],
                  ['STATUS', 'justify-start'],
                  ['LAST UPDATED', 'justify-start'],
                  ['ACTIONS', 'justify-end text-right'],
                ] as const
              ).map(([label, align]) => (
                <div key={label} className={['flex px-6 py-[14px]', align].join(' ')}>
                  <span className="text-[11px] font-bold uppercase leading-[16.5px] tracking-[0.55px] text-[#6b7280]">
                    {label}
                  </span>
                </div>
              ))}
            </div>

            {/* Body */}
            {pageRows.length > 0 ? (
              pageRows.map((dept) => (
                <DepartmentRow
                  key={dept.id}
                  dept={dept}
                  onEdit={() => navigate('/admin/dept-configuration/new')}
                />
              ))
            ) : (
              <p className="px-6 py-12 text-center text-sm text-ink-secondary">
                No departments match your filters.
              </p>
            )}
          </div>
        </div>

        {/* Pagination */}
        <Pagination
          rangeStart={total === 0 ? 0 : (page - 1) * pageSize + 1}
          rangeEnd={Math.min(page * pageSize, total)}
          total={total}
          noun="departments"
          page={page}
          totalPages={totalPages}
          onChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(v) => {
            setPageSize(v);
            setPage(1);
          }}
        />
      </div>
    </div>
  );
}
